// Opening the right page from a push notification tap.
// The service worker (public/sw.js) records the tapped URL in Cache Storage
// and also posts it to an open window. iOS home-screen apps do not reliably
// honour client.navigate()/openWindow() URLs, so the app itself reads the
// pending URL on start and whenever it comes back to the foreground.
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const PUSH_NAV_CACHE = "br-push-nav";
export const PUSH_NAV_KEY = "/__br_push_nav";
const MAX_AGE_MS = 10 * 60 * 1000;

function sameOriginPath(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return u.pathname + u.search + u.hash;
  } catch {
    return null;
  }
}

export async function takePendingPushUrl() {
  if (typeof caches === "undefined") return null;
  try {
    const cache = await caches.open(PUSH_NAV_CACHE);
    const res = await cache.match(PUSH_NAV_KEY);
    if (!res) return null;
    await cache.delete(PUSH_NAV_KEY);
    const { url, at } = await res.json();
    if (!url || Date.now() - (at || 0) > MAX_AGE_MS) return null;
    return sameOriginPath(url);
  } catch {
    return null;
  }
}

export function usePushNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    const go = (path) => {
      if (!alive || !path) return;
      const here = window.location.pathname + window.location.search + window.location.hash;
      if (path !== here) navigate(path);
    };
    const check = () => takePendingPushUrl().then(go);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const onMessage = (e) => {
      if (e.data && e.data.type === "br-push-navigate") {
        // The cached copy is for cold starts; clear it so it is not replayed.
        takePendingPushUrl();
        go(sameOriginPath(e.data.url));
      }
    };
    check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : null;
    if (sw) sw.addEventListener("message", onMessage);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      if (sw) sw.removeEventListener("message", onMessage);
    };
  }, [navigate]);
}

export function PushNavigation() {
  usePushNavigation();
  return null;
}
